import { UserTaskActor, UserTaskStepCode, UserTaskStepStatus } from "../interfaces/DTO/userTask/UserTaskWorkflow";
import { formatCurrency } from "./namesFormater";

export function formatStepCode(code: string): string {
  const map: Record<string, string> = {
    CHECK_REQUIRED_DOCUMENTS: "בדיקת מסמכים נדרשים",
    COLLECT_MISSING_DOCUMENTS: "איסוף מסמכים חסרים",
    PREPARE_FORMS: "הכנת טפסים",
    CLIENT_SIGNATURE: "חתימת לקוח",
    TRANSFER_FORM_SIGNATURE: "חתימת לקוח על טופסי ניוד",
    REQUEST_FORM_SIGNATURE: "חתימת לקוח על טופס הבקשה",
    GENERATE_SIGNED_PDF: "יצירת PDF חתום",
    SHOW_BANK_DETAILS: "הצגת פרטי חשבון בנק",
    UPLOAD_TRANSFER_CONFIRMATION: "העלאת אישור העברה",
    UPLOAD_ID_DOCUMENT: "צילום תעודת זהות",
    SEND_TO_COMPANY: "שליחה לחברה",
    WAIT_COMPANY_PROCESSING: "המתנה לעיבוד חברה",
    VERIFY_DAILY_REPORT: "אימות דוח יומי",
    VERIFY_MIMSHAK_DATA: "אימות נתוני פרודוקציה בחודש הבא",
  };
  return map[code] ?? code.replace(/_/g, " ");
}

/** the steps whose completion stores a document (result_data.document_path) - and its button's label */
export const STEP_DOCUMENT_LABELS: Partial<Record<string, string>> = {
  [UserTaskStepCode.CLIENT_SIGNATURE]: "צפייה במסמך החתום",
  [UserTaskStepCode.REQUEST_FORM_SIGNATURE]: "צפייה במסמך החתום",
  [UserTaskStepCode.TRANSFER_FORM_SIGNATURE]: "צפייה במסמך החתום",
  [UserTaskStepCode.UPLOAD_TRANSFER_CONFIRMATION]: "צפייה באישור ההעברה",
  [UserTaskStepCode.UPLOAD_ID_DOCUMENT]: "צפייה בצילום תעודת הזהות",
};

// the steps whose document the agent can attach (a file the client sent) - and the button's label
export const STEP_UPLOAD_LABELS: Partial<Record<string, string>> = {
  [UserTaskStepCode.UPLOAD_TRANSFER_CONFIRMATION]: "העלה אישור העברה",
  [UserTaskStepCode.UPLOAD_ID_DOCUMENT]: "העלה צילום תעודת זהות",
};

export function formatStepStatus(status: UserTaskStepStatus): string {
  switch (status) {
    case UserTaskStepStatus.BLOCKED:
      return "ממתין";
    case UserTaskStepStatus.PENDING:
      return "פעיל";
    case UserTaskStepStatus.IN_PROGRESS:
      return "בביצוע";
    case UserTaskStepStatus.WAITING:
      return "ממתין לתשובה";
    case UserTaskStepStatus.COMPLETED:
      return "הושלם";
    case UserTaskStepStatus.FAILED:
      return "נכשל";
    case UserTaskStepStatus.SKIPPED:
      return "דולג";
    case UserTaskStepStatus.CANCELLED:
      return "בוטל";
    default:
      return status;
  }
}

export function formatActor(actor: UserTaskActor): string {
  switch (actor) {
    case UserTaskActor.CLIENT:
      return "לקוח";
    case UserTaskActor.AGENT:
      return "סוכן";
    case UserTaskActor.SYSTEM:
      return "מערכת";
    case UserTaskActor.EXTERNAL:
      return "גורם חיצוני";
    default:
      return actor;
  }
}

export function colorStepStatus(status: UserTaskStepStatus): string {
  switch (status) {
    case UserTaskStepStatus.COMPLETED:
      return "#4caf50";
    case UserTaskStepStatus.PENDING:
    case UserTaskStepStatus.IN_PROGRESS:
      return "#1976d2";
    case UserTaskStepStatus.WAITING:
      return "#ff9800";
    case UserTaskStepStatus.FAILED:
      return "#f44336";
    case UserTaskStepStatus.SKIPPED:
      return "#9c27b0";
    case UserTaskStepStatus.CANCELLED:
      return "#9e9e9e";
    case UserTaskStepStatus.BLOCKED:
    default:
      return "#bdbdbd";
  }
}

export function colorActor(actor: UserTaskActor): string {
  switch (actor) {
    case UserTaskActor.CLIENT:
      return "#1976d2";
    case UserTaskActor.AGENT:
      return "#388e3c";
    case UserTaskActor.SYSTEM:
      return "#7b1fa2";
    case UserTaskActor.EXTERNAL:
      return "#f57c00";
    default:
      return "#9e9e9e";
  }
}

export function isActiveStep(status: UserTaskStepStatus): boolean {
  return [
    UserTaskStepStatus.PENDING,
    UserTaskStepStatus.IN_PROGRESS,
    UserTaskStepStatus.WAITING,
  ].includes(status);
}

// ---------- the Simulation Portal - the client's wording ----------

/** what the client does / sees in the step - the client's wording (not the agent's step names) */
export function formatPortalStepName(stepCode: string): string {
  const names: Record<string, string> = {
    CLIENT_SIGNATURE: "חתימה על הבקשה",
    TRANSFER_FORM_SIGNATURE: "חתימה על הטפסים",
    REQUEST_FORM_SIGNATURE: "חתימה על הטפסים",
    SHOW_BANK_DETAILS: "ביצוע ההפקדה בהעברה בנקאית",
    UPLOAD_TRANSFER_CONFIRMATION: "צירוף אישור ההעברה",
    UPLOAD_ID_DOCUMENT: "צירוף צילום תעודת זהות",
    WAIT_COMPANY_PROCESSING: "טיפול בבקשה בחברה המנהלת",
  };
  return names[stepCode] ?? "שלב בבקשה";
}

/** the active step's details - what the client should do now */
export function formatPortalStepDetails(stepCode: string, amount?: number | null): string | null {
  switch (stepCode) {
    case "CLIENT_SIGNATURE":
      return "יש לחתום כדי לאשר את הבקשה. המסמך יוצג לאישור לפני השליחה.";
    case "TRANSFER_FORM_SIGNATURE":
    case "REQUEST_FORM_SIGNATURE":
      // active - the agent prepared the forms; the forms system sends them (usually one link for all)
      return "הטפסים לחתימה נשלחו אליך ב-SMS. לאחר החתימה הסוכן/ת יעדכן/תעדכן כאן.";
    case "SHOW_BANK_DETAILS":
      return amount != null
        ? `יש להעביר ${formatCurrency(amount)} לחשבון בחברה המנהלת – פרטי ההעברה למטה.`
        : "יש לבצע העברה לחשבון בחברה המנהלת – פרטי ההעברה למטה.";
    case "UPLOAD_TRANSFER_CONFIRMATION":
      return 'אחרי ההעברה – לחצו "ביצעתי" וצרפו צילום של אישור הבנק.';
    case "UPLOAD_ID_DOCUMENT":
      return "לפתיחת הקופה יש לצרף צילום של תעודת הזהות – למטה.";
    case "WAIT_COMPANY_PROCESSING":
      return "הבקשה נשלחה לחברה המנהלת ונמצאת בטיפול.";
    default:
      return null;
  }
}

/** a task's status - the client's wording */
export function formatPortalTaskStatus(status: string): string {
  switch (status) {
    case "PENDING":
      return "ממתינה";
    case "IN_PROGRESS":
      return "בטיפול";
    case "COMPLETED":
      return "הושלמה";
    case "FAILED":
      return "נכשלה";
    case "IGNORED":
      return "בוטלה";
    case "REJECTED":
      return "נדחתה";
    default:
      return status;
  }
}

// signing forms the agent prepares in an external forms system - the portal shows their status only
export const FORMS_SIGNATURE_STEPS = new Set(["TRANSFER_FORM_SIGNATURE", "REQUEST_FORM_SIGNATURE"]);

/** a step's status - for signing forms, in the client's terms (prepared? signed?) */
export function formatPortalStepStatusForStep(stepCode: string, status: string): string {
  if (FORMS_SIGNATURE_STEPS.has(stepCode)) {
    if (status === "BLOCKED") return "הסוכן/ת יכין/תכין עבורך טפסים לחתימה";
    if (status === "COMPLETED") return "החתימה התקבלה";
  }
  // a deposit into a new fund - transferred once the fund is opened
  if (stepCode === "SHOW_BANK_DETAILS" && status === "BLOCKED") return "פרטי ההעברה יוצגו כשהקופה תיפתח";
  return formatPortalStepStatus(status);
}

/** a step's status - the client's wording (the agent's - formatStepStatus) */
export function formatPortalStepStatus(status: string): string {
  switch (status) {
    case "BLOCKED":
      return "בהמשך";
    case "PENDING":
      return "השלב הבא שלך";
    case "IN_PROGRESS":
      return "בביצוע";
    case "WAITING":
      return "ממתין לתשובה";
    case "COMPLETED":
      return "הושלם";
    case "FAILED":
      return "נכשל";
    case "SKIPPED":
      return "דולג";
    case "CANCELLED":
      return "בוטל";
    default:
      return status;
  }
}

/** a step status that came as a plain string (e.g. from the portal's API) is active */
export const isActiveStepStatus = (status: string): boolean => isActiveStep(status as UserTaskStepStatus);
